import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  dialogCloseIconSx,
  dialogPaperSx,
  dialogTitleSx,
  fieldSx,
  goldCheckboxSx,
  outlinedBrandBtnSx,
  primaryBtnSx,
} from "./schedulerDialogStyles";
import {
  buildStylistDaySheet,
  stylistDaySheetFileName,
} from "./stylistDaySheet";
import { printPdfBlob, renderStylistDaySheetPdf } from "./stylistDaySheetPdf";

dayjs.extend(utc);
dayjs.extend(timezone);

const PrintStylistScheduleDialog = ({
  open,
  onClose,
  stylists = [],
  events = [],
  selectedDate,
  timeZone,
  locationName = "",
  salonAddress = "",
}) => {
  const [stylistId, setStylistId] = useState("");
  const [includeCanceled, setIncludeCanceled] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIncludeCanceled(false);
    setStylistId(stylists.length === 1 ? String(stylists[0].user_id) : "");
  }, [open, stylists]);

  const stylist = stylists.find((item) => String(item.user_id) === String(stylistId));
  const dateLabel = selectedDate
    ? dayjs(selectedDate).tz(timeZone).format("dddd, MMMM D, YYYY")
    : "";

  const preview = useMemo(() => {
    if (!stylist || !selectedDate) return null;
    return buildStylistDaySheet({
      stylist,
      events,
      selectedDate,
      timeZone,
      includeCanceled,
      locationName,
      salonAddress,
    });
  }, [
    stylist,
    events,
    selectedDate,
    timeZone,
    includeCanceled,
    locationName,
    salonAddress,
  ]);

  const outputPdf = () => {
    if (!preview) return null;
    return renderStylistDaySheetPdf(preview);
  };

  const handleDownload = () => {
    const pdf = outputPdf();
    if (!pdf) return;
    pdf.save(stylistDaySheetFileName(preview));
  };

  const handlePrint = () => {
    const pdf = outputPdf();
    if (!pdf) return;
    printPdfBlob(pdf.output("blob"));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={dialogTitleSx}>
        <Box sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>Print day sheet</Box>
        <IconButton onClick={onClose} sx={dialogCloseIconSx} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 1.5, mb: 2 }}>
          One stylist’s appointments for the day currently shown on the scheduler.
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>{dateLabel}</Typography>
        <TextField
          select
          fullWidth
          size="small"
          label="Stylist"
          value={stylistId}
          onChange={(event) => setStylistId(event.target.value)}
          sx={fieldSx}
          disabled={!stylists.length}
        >
          {stylists.map((item) => (
            <MenuItem key={item.user_id} value={String(item.user_id)}>
              {item.user_name}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          sx={{ mt: 1.5, ml: 0 }}
          control={
            <Checkbox
              size="small"
              checked={includeCanceled}
              onChange={(event) => setIncludeCanceled(event.target.checked)}
              sx={goldCheckboxSx}
            />
          }
          label="Include canceled appointments"
        />
        <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1 }}>
          {preview
            ? `${preview.appointmentCount} appointment${
                preview.appointmentCount === 1 ? "" : "s"
              } · ${preview.bookedLabel} booked${
                preview.shiftLabel ? ` · shift ${preview.shiftLabel}` : ""
              }`
            : "Choose a stylist to preview the day."}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleDownload}
          disabled={!preview}
          sx={outlinedBrandBtnSx}
        >
          Download PDF
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={!preview}
          sx={primaryBtnSx}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintStylistScheduleDialog;
